/*
 * Lemonade Training Oy, copy rights, all right reserved.
 */
import { useEffect, useState } from "react";
import { onSnapshot, type FirestoreError, type QuerySnapshot } from "firebase/firestore";

import { pendingDecisionCountQuery, readRequestDocs } from "../lib/membershipRequestQueries";

export interface PendingRequestCount {
  count: number;
  /** True when the listener failed — the count below is NOT a real zero. */
  failed: boolean;
}

/**
 * Live count of requests waiting on the gym, for the sidebar badge.
 *
 * Its own slim listener rather than a slice of the queue page's: the badge is
 * mounted on every dashboard route, so it reads one capped page of ids and
 * nothing else. The cap is one past the display cap, which is all "20+" needs
 * to know.
 *
 * A listener error is reported, never folded into zero. A stale claim
 * (permission-denied) or an index still building (failed-precondition) would
 * otherwise be indistinguishable from an empty queue — and since the badge is
 * the only way a gym learns of a request without opening the page, requests
 * would pile up behind a confident zero.
 */
export function usePendingRequestCount(boxID: string | null | undefined): PendingRequestCount {
  const [state, setState] = useState<PendingRequestCount>({ count: 0, failed: false });

  useEffect(() => {
    if (!boxID) {
      setState({ count: 0, failed: false });
      return;
    }

    let reported = false;
    const unsubscribe = onSnapshot(
      pendingDecisionCountQuery(boxID),
      // The same guard the list applies, so the badge cannot claim three while
      // the page shows two. Filtering one capped page is free.
      (snapshot: QuerySnapshot) =>
        setState({ count: readRequestDocs(snapshot.docs, "badge").length, failed: false }),
      (error: FirestoreError) => {
        if (!reported) {
          reported = true;
          console.warn("pending membership request count unavailable", { code: error.code });
        }
        setState({ count: 0, failed: true });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [boxID]);

  return state;
}
